import { ExternalLink } from "lucide-react";
import { CLIENT_TYPE_PROFILES, type ClientTypeValue } from "@school-sales-ai/shared";
import { useConversationScripts } from "@/hooks/useConversationScripts";
import { WizardFooter } from "./WizardFooter";

/**
 * Steps 5 and 6, done in the pages that already do them.
 *
 * The deployment and the test agent both have working UI today; what they do not have yet
 * is the guided version the wizard is meant to provide (phase 3). Rather than fake those
 * steps or leave a wizard that can never finish, each one points at the page that does the
 * work and lets the operator mark it done — the work is real, only the automation is
 * missing. Step 4 graduated out of here once its AI draft landed.
 *
 * Marking done is a deliberate human claim, not a check the wizard can make: whether an
 * agent answers well enough to face real prospects is a judgement call.
 *
 * The link opens in a new tab so the wizard keeps its place, and the target page reads
 * the client from the switcher — which the wizard has pinned to this client, so it
 * lands on the right tenant.
 */

type ManualStepInfo = {
  what: string;
  where: string;
  linkLabel: string;
  href: (clientId: string) => string;
};

const MANUAL_STEPS: Record<number, ManualStepInfo> = {
  5: {
    what: "Elige el script de conversación y define la identidad del agente: su nombre y su tono (cómo habla: de usted o de tú, formal o cercano). Esto crea su primer deployment, que es lo que lo pone a hablar.",
    where: "Se configura en el editor de deployment del cliente.",
    linkLabel: "Abrir deployment",
    href: (clientId) => `/super-admin/clients/${clientId}/deployment`,
  },
  6: {
    what: "Conversa con el agente como lo haría un prospecto y revisa que responda con el catálogo y la base de conocimiento correctos.",
    where: "Se prueba en Agente → Probar agente, que corre el flujo real marcado como prueba.",
    linkLabel: "Abrir Agente",
    href: () => "/agent",
  },
};

type Props = {
  step: number;
  clientId: string;
  /** The client's segment — step 5 names the script that segment usually runs. */
  clientType: ClientTypeValue | null;
  onBack: (() => void) | null;
  onDone: () => void;
  onSkip: () => void;
  isBusy: boolean;
};

export function StepManual({
  step,
  clientId,
  clientType,
  onBack,
  onDone,
  onSkip,
  isBusy,
}: Props): React.JSX.Element | null {
  const info = MANUAL_STEPS[step];
  if (!info) return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-ink-100 bg-white p-4">
        <p className="text-[13px] text-ink-900">{info.what}</p>
        <p className="mt-2 text-[12px] text-ink-500">{info.where}</p>
        {step === 5 && clientType && <SuggestedScript clientType={clientType} />}
        <a
          href={info.href(clientId)}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-ink-200 px-3 py-2 text-xs font-medium text-moss hover:bg-ink-050"
        >
          <ExternalLink size={13} />
          {info.linkLabel}
        </a>
      </div>

      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="max-w-lg text-[12px] text-ink-400">
          Este paso todavía no está automatizado dentro del wizard. Márcalo como listo
          cuando lo hayas configurado; si lo saltas, el onboarding seguirá marcado como
          incompleto.
        </p>
        <button
          type="button"
          onClick={onSkip}
          disabled={isBusy}
          className="text-xs font-medium text-ink-500 hover:text-moss disabled:opacity-40"
        >
          Saltar por ahora
        </button>
      </div>

      <WizardFooter
        onBack={onBack}
        onContinue={onDone}
        canContinue
        isBusy={isBusy}
        continueLabel="Marcar como listo"
      />
    </div>
  );
}

/**
 * The script the client's segment usually runs, by name. A hint, never applied: the
 * operator picks the script in the deployment editor, and only they can tell whether a
 * client is the usual case.
 */
function SuggestedScript({ clientType }: { clientType: ClientTypeValue }): React.JSX.Element {
  const useCase = CLIENT_TYPE_PROFILES[clientType].suggestedScriptUseCase;
  const { data: scripts } = useConversationScripts({ status: "active" });

  if (!useCase) {
    return (
      <p className="mt-2 text-[12px] text-amber-800">
        Todavía no hay un script para este tipo de cliente. Elige el más cercano a cómo
        vende el cliente.
      </p>
    );
  }

  // The useCase is the script's identity; its name is what the deployment editor shows.
  const name = scripts?.find((s) => s.useCase === useCase)?.name ?? useCase;
  return (
    <p className="mt-2 text-[12px] text-ink-700">
      Script sugerido para este tipo de cliente: <strong>{name}</strong>.
    </p>
  );
}
